import { Fragment } from 'react/jsx-runtime';

import InHosWhite from '@/assets/images/logo/inhos-white.webp';
import SanurWhite from '@/assets/images/logo/sanur-white.webp';
import { NewsletterSubscriptionForm } from '@/features/newsletter';
import { useFooterData } from '@/shared/hooks/useFooterData';
import type { FooterData } from '@/shared/types/footer';

import {
  FaFacebook,
  FaHeadphones,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaWhatsapp,
  FaYoutube,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { footerData } = useFooterData();

  // Fallback data in case API fails or is loading
  const defaultData: FooterData = {
    address:
      'Jl. Hang Tuah No.35, Sanur Kaja, Denpasar Selatan, Kota Denpasar, Bali 80227, Indonesia',
    hotline: '1500629',
    email: 'string@string.coms',
    whatsapp: '+6281139908899',
    instagram: '',
    facebook: '',
    youtube: '',
    x: '',
    linkedin: '',
    maps: {
      title: 'The Sanur',
      src: 'https://cloud.3dvista.com//hosting/6111982/126/index.htm',
      width: '1500px',
      height: '800px',
    },
  };

  const data: FooterData = footerData || defaultData;

  return (
    <Fragment>
      <div className='h-auto bg-[#8F8A83] text-white py-8 lg:py-10 lg:px-[110px] px-5'>
        <div className='hidden lg:grid grid-cols-5 gap-x-[60px]'>
          <div className='flex flex-col gap-y-8 py-8'>
            <img
              src={InHosWhite}
              alt='InJourney Hospitality White Logo'
              className='w-[180px]'
            />
            <img
              src={SanurWhite}
              alt='Sanur White Logo'
              className='w-[180px]'
            />
            <div className='flex gap-x-5 items-center text-[20pt]'>
              {data.instagram && (
                <a
                  href={data.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Instagram'
                >
                  <FaInstagram />
                </a>
              )}
              {data.facebook && (
                <a
                  href={data.facebook}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Facebook'
                >
                  <FaFacebook />
                </a>
              )}
              {data.youtube && (
                <a
                  href={data.youtube}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='YouTube'
                >
                  <FaYoutube />
                </a>
              )}
              {data.x && (
                <a
                  href={data.x}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Twitter'
                >
                  <FaTwitter />
                </a>
              )}
              {data.linkedin && (
                <a
                  href={data.linkedin}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='LinkedIn'
                >
                  <FaLinkedin />
                </a>
              )}
            </div>
          </div>
          <div className='flex flex-col gap-y-[24px]'>
            <div className='flex flex-col gap-y-[16px]'>
              <span
                className='font-bold text-[15pt] cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Information
              </span>
              <div
                className='flex flex-col gap-y-[18px] font-light text-[16px]'
                style={{
                  fontFamily: 'Figtree',
                }}
              >
                <Link
                  to='/about'
                  reloadDocument
                  className='hover:opacity-80 transition-opacity'
                >
                  About Us
                </Link>
                <Link
                  to='/sustainability'
                  reloadDocument
                  className='hover:opacity-80 transition-opacity'
                >
                  Sustainability
                </Link>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-y-[24px]'>
            <div className='flex flex-col gap-y-[16px]'>
              <span
                className='font-bold text-[15pt] cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Contact Us
              </span>
              <div className='flex flex-col gap-y-[18px] font-light text-[16px]'>
                <button
                  type='button'
                  className='flex gap-x-2 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  onClick={() => {
                    if (data.hotline) {
                      window.location.href = `tel:${data.hotline.replace(/\s+/g, '')}`;
                    }
                  }}
                  aria-label='Call hotline'
                >
                  <FaHeadphones aria-hidden='true' />
                  <span>{data.hotline}</span>
                </button>
                <button
                  type='button'
                  className='flex gap-x-2 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  onClick={() => {
                    if (data.whatsapp) {
                      // Remove non-digit characters for WhatsApp link
                      const phone = data.whatsapp.replace(/\D+/g, '');
                      window.open(
                        `https://wa.me/${phone}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }
                  }}
                  aria-label='Chat on WhatsApp'
                >
                  <FaWhatsapp aria-hidden='true' />
                  <span>{data.whatsapp}</span>
                </button>
              </div>
            </div>
          </div>
          <div className='flex flex-col gap-y-[24px]'>
            <div className='flex flex-col gap-y-[16px]'>
              <span
                className='font-bold text-[15pt] cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Address
              </span>
              <div
                className='font-light text-[16px]'
                style={{
                  fontFamily: 'Figtree',
                }}
              >
                <span className='font-light'>
                  The Sanur
                  <br />
                  {data.address}
                </span>
              </div>
            </div>
            <div>
              <a
                href='http://wbs.injourneyhospitality.id'
                target='_blank'
                className='font-bold text-[15pt]'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Whistleblowing System
              </a>
            </div>
          </div>
          <div className='flex flex-col gap-y-4'>
            <span
              className='font-bold text-[15pt] cursor-default'
              style={{
                fontFamily: 'AngieSansStd',
              }}
            >
              Subscribe to Our Newsletter
            </span>
            <NewsletterSubscriptionForm />
          </div>
        </div>
        <div className='lg:hidden flex flex-col w-full space-y-8'>
          {/* Mobile/Tablet Navigation Links */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8'>
            <div className='space-y-4'>
              <h3
                className='font-bold text-sm cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Information
              </h3>
              <div
                className='flex flex-col space-y-3 text-sm'
                style={{
                  fontFamily: 'Figtree',
                }}
              >
                <Link
                  to='/about'
                  reloadDocument
                  className='hover:opacity-80 transition-opacity'
                >
                  About Us
                </Link>
                <Link
                  to='/sustainability'
                  reloadDocument
                  className='hover:opacity-80 transition-opacity'
                >
                  Sustainability
                </Link>
              </div>
            </div>

            <div className='space-y-4'>
              <h3
                className='font-bold text-sm cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Contact Us
              </h3>
              <div
                className='flex flex-col space-y-3 text-sm'
                style={{
                  fontFamily: 'Figtree',
                }}
              >
                <div className='flex items-center space-x-2'>
                  <FaHeadphones className='text-xs' />
                  <span>{data.hotline}</span>
                </div>
                <div className='flex items-center space-x-2'>
                  <FaWhatsapp className='text-xs' />
                  <span>{data.whatsapp}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Address and Additional Info Section */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8'>
            <div className='space-y-4'>
              <h3
                className='font-bold text-sm cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Address
              </h3>
              <div
                className='text-sm leading-relaxed'
                style={{
                  fontFamily: 'Figtree',
                }}
              >
                <span>
                  The Sanur
                  <br />
                  {data.address}
                </span>
              </div>
            </div>

            <div className='space-y-4'>
              <h3
                className='font-bold text-sm cursor-default'
                style={{
                  fontFamily: 'AngieSansStd',
                }}
              >
                Subscribe to Our Newsletter
              </h3>
              <NewsletterSubscriptionForm
                className='w-full'
                placeholder='e-mail address'
              />
            </div>
          </div>

          {/* Whistleblowing System */}
          <div className='text-center md:text-left'>
            <a
              href='http://wbs.injourneyhospitality.id'
              target='_blank'
              rel='noopener noreferrer'
              className='font-bold text-sm hover:opacity-80 transition-opacity'
              style={{
                fontFamily: 'AngieSansStd',
              }}
            >
              Whistleblowing System
            </a>
          </div>

          {/* Logos and Social Media */}
          <div className='flex flex-col items-center space-y-6 pt-4'>
            <div className='flex flex-col items-center space-y-4'>
              <img
                src={InHosWhite}
                alt='InJourney Hospitality White Logo'
                className='w-40 h-auto'
              />
              <img
                src={SanurWhite}
                alt='Sanur White Logo'
                className='w-40 h-auto'
              />
            </div>
            <div className='flex items-center space-x-4 text-lg'>
              {data.instagram && (
                <a
                  href={data.instagram}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Instagram'
                >
                  <FaInstagram />
                </a>
              )}
              {data.facebook && (
                <a
                  href={data.facebook}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Facebook'
                >
                  <FaFacebook />
                </a>
              )}
              {data.youtube && (
                <a
                  href={data.youtube}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='YouTube'
                >
                  <FaYoutube />
                </a>
              )}
              {data.x && (
                <a
                  href={data.x}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='Twitter'
                >
                  <FaTwitter />
                </a>
              )}
              {data.linkedin && (
                <a
                  href={data.linkedin}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:opacity-80 transition-opacity'
                  aria-label='LinkedIn'
                >
                  <FaLinkedin />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Footer;
